import { useState, useRef, useEffect } from 'react';
import { Star, ArrowLeft, ArrowRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

type TestimonialType = {
  id: string;
  text: string;
  author: string;
  role: string;
  type: 'creator' | 'brand';
  avatar?: string;
  company?: string;
  companyLogo?: string;
};

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  const testimonials: TestimonialType[] = [
    {
      id: '1',
      text: "Since joining Woost, I've worked with at least 3-4 brands each month. The platform makes it super easy to apply to campaigns and connect with great brands. Payments and product shipping are always on time!",
      author: "Priya Sharma",
      role: "Fitness Influencer",
      type: "creator",
      avatar: "/images/female.jpg"
    },
    {
      id: '2',
      text: "Since running campaigns on Woost, driving sales has become so much easier. Managing everything and tracking results is smoother and more effective than ever. Honestly, Woost has taken our brand’s productivity to the next level!",
      author: "kshitij Ranjan",
      role: "Head of Growth",
      company: "Koparoclean",
      companyLogo: "",
      type: "brand",
      avatar: "/images/male.png"
    },
    {
      id: '3',
      text: "As a nano influencer, I love how Woost values authentic storytelling. They match me with brands that align perfectly with my content style and values.",
      author: "Arjun Kumar",
      role: "Skincare Influencer",
      type: "creator",
      avatar: "/images/male.png"
    },
    {
      id: '4',
      text: "The authenticity of content created through Woost's platform has helped us build genuine connections with our target audience. Our engagement rates have increased by 45% since we started working with their creators.",
      author: "Sumit Batra",
      role: "Co-Founder",
      company: "Klenzmo",
      companyLogo: "",
      type: "brand",
      avatar: "/images/male.png"
    },

    {
      id: '5',
      text: "The platform's analytics help us track ROI in real-time. We've seen a consistent 1.8x return on our influencer marketing spend through Woost.",
      author: "Dev",
      role: "Digital Marketing Manager",
      company: "Asaya",
      companyLogo: "",
      type: "brand",
      avatar: "/images/male.png"
    }
  ];
  
  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };
  
  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  // Automatic rotation
  useEffect(() => {
    const timer = setInterval(() => {
      nextTestimonial();
    }, 6000);
    
    return () => clearInterval(timer);
  }, [currentTestimonial]);

  // Counter for appRating animation
  const [appRating, setAppRating] = useState(0);
  
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setAppRating(prev => {
          if (prev < 4.2) {
            return parseFloat((prev + 0.1).toFixed(1));
          } else {
            clearInterval(interval);
            return 4.2;
          }
        });
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [isVisible]);
  
  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Testimonials Column */}
          <div className="lg:w-2/3">
            <motion.div 
              className="transition-all duration-700"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Testimonials</h2>
              
              <div className="bg-gray-50 rounded-2xl p-8 md:p-10 mt-8 relative">
                <Quote className="absolute top-6 left-6 text-woost-yellow opacity-20" size={48} />
                
                <div className="relative z-10">
                  <AnimatePresence mode="wait">
                    {testimonials.map((testimonial, index) => (
                      index === currentTestimonial && (
                        <motion.div
                          key={testimonial.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.4 }}
                        >
                          <blockquote className="text-xl md:text-2xl text-gray-700 mb-6">
                            "{testimonial.text}"
                          </blockquote>
                          
                          <div className="flex items-center">
                            {testimonial.avatar && (
                              <div className="mr-4">
                                <img 
                                  src={testimonial.avatar} 
                                  alt={testimonial.author} 
                                  className="w-14 h-14 rounded-full object-cover border-2 border-woost-yellow"
                                />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-lg">{testimonial.author}</p>
                                {testimonial.companyLogo && (
                                  <img 
                                    src={testimonial.companyLogo} 
                                    alt={testimonial.company} 
                                    className="h-5"
                                  />
                                )}
                              </div>
                              <p className="text-gray-500">
                                {testimonial.role}
                                {testimonial.company && `, ${testimonial.company}`}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <span className={`inline-block ${
                              testimonial.type === 'creator' 
                                ? 'bg-woost-soft-yellow text-woost-yellow'
                                : 'bg-woost-soft-blue text-woost-blue'
                            } px-3 py-1 rounded-full text-sm font-medium`}>
                              {testimonial.type === 'creator' ? 'Creator Testimonial' : 'Brand Testimonial'}
                            </span>
                          </div>
                        </motion.div>
                      )
                    ))}
                  </AnimatePresence>
                </div>
                
                <div className="flex justify-between mt-8">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={prevTestimonial}
                    className="rounded-full"
                  >
                    <ArrowLeft size={18} />
                  </Button>
                  <div className="flex gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentTestimonial 
                            ? 'bg-woost-yellow' 
                            : 'bg-gray-300'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                      />
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={nextTestimonial}
                    className="rounded-full"
                  >
                    <ArrowRight size={18} />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* App Rating Column */}
          <div className="lg:w-1/3">
            <motion.div 
              className="bg-woost-soft-yellow p-8 rounded-2xl text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">Loved by Creators</h3>
                <p className="text-gray-600">Join thousands of creators who love using the Woost app</p>
              </div>
              
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={32} className="text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-3xl font-bold mb-2"
              >
                {appRating.toFixed(1)} / 5.0
              </motion.div>
              
              <p className="text-gray-600 mb-6">1000+ reviews</p>
              
              <div className="flex flex-col gap-3">
              <a 
                    href="https://play.google.com/store/apps/details?id=com.woost" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className=" text-white px-4 py-2 rounded-lg h-12 flex items-center justify-center gap-2 transition-colors"
                  >
        
                  <img src="/images/playstore.png" alt="playstore logo" className='h-12'/>
                </a>
                
                <div className="text-gray-600 font-medium">
                  25,000+ creators have joined
                </div>
                
                <div className="flex justify-center pt-4">
                  <div className="flex -space-x-4">
                    {[...Array(5)].map((_, i) => (
                      <img 
                        key={i}
                        className="w-10 h-10 border-2 border-white rounded-full dark:border-gray-800" 
                        src={`https://i.pravatar.cc/150?img=${10 + i}`} 
                        alt={`Creator ${i + 1}`}
                      />
                    ))}
                    <div className="flex items-center justify-center w-10 h-10 text-xs font-medium text-white bg-woost-blue rounded-full border-2 border-white">
                      +25K
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
